/**
 * Shared class strings for the segment condition builders (pills, chips, condition rows).
 *
 * These were duplicated verbatim across FilterValueBuilder, SegmentConditionFilterLayout and
 * CustomerTagSegmentCreationFlow, so the three drifted apart easily. Kept as plain strings rather
 * than components, matching `packages/ui/src/lib/menu-item-classes.ts`.
 *
 * The two `FIELD_PILL_*` variants differ on purpose: the dynamic flow's field pill carries side
 * borders (its field is user-selected), the customer-tag flow's does not (its field is always
 * `Tags`). Keeping both here makes that a deliberate choice rather than two files quietly diverging.
 */

/** Operator / value pill that opens a selector. Filled, hover, pointer. */
export const PILL_INTERACTIVE_CLASS =
  'flex whitespace-nowrap cursor-pointer items-center gap-1 px-1 py-0.5 rounded-md text-text-sm-medium text-zinc-600 transition-colors bg-zinc-950/5 hover:bg-zinc-950/8 outline-none shrink-0'

/** Same shape as `PILL_INTERACTIVE_CLASS` in an error state — mirrors the static list's value error. */
export const PILL_ERROR_CLASS =
  'flex whitespace-nowrap cursor-pointer items-center gap-1 px-1 py-0.5 rounded-md text-text-sm-medium text-red-600 transition-colors bg-red-600/10 hover:bg-red-600/15 outline-none shrink-0'

/** Read-only value pill — no pointer, no hover. */
export const TAG_PILL_CLASS =
  'flex whitespace-nowrap items-center gap-1 px-1 py-0.5 rounded-md text-text-sm-medium text-zinc-600 bg-zinc-950/5 transition-colors shrink-0'

/**
 * Per-condition horizontal inset plus row hover. Carries its own `px-8`, so the scrolling parent
 * must not add horizontal padding of its own.
 */
export const CONDITION_CONTAINER_PAD_HOVER_CLASS = 'px-8 transition-colors hover:bg-zinc-500/[0.04]'

/** Read-only manager view — same inset, no hover affordance. */
export const CONDITION_CONTAINER_PAD_STATIC_CLASS = 'px-8'

/** Field pill — plain, for a flow whose field is fixed (customer-tag). */
export const FIELD_PILL_CLASS = 'flex items-center gap-1 px-1 py-0.5 rounded-md w-fit shrink-0'

/** Field pill — side borders, for a flow whose field is selectable (dynamic). */
export const FIELD_PILL_BORDERED_CLASS =
  'flex items-center gap-1 px-1 py-0.5 border-l-1 border-r-1 border-zinc-950/10 rounded-md w-fit shrink-0'
